import { useEffect, useMemo, useState } from "react";
import { FileUp, FileText, Loader2, Trash2, Upload, Filter, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Upload + manage arbitrary "outside HomeSchool Hub" documents on a student's
 * profile — worksheets, art, book reports, project photos, journals, tests, etc.
 * Everything here flows into the year-end Evaluation Packet automatically.
 *
 * Companion to AssessmentsPanel (WRAT-5) but for freeform artifacts.
 */
export default function PortfolioDocumentsPanel({ studentId, subjects = [] }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("__all__");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", category: "worksheet", subject: "",
    document_date: new Date().toISOString().slice(0, 10),
    notes: "", file: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/portfolio-docs?student_id=${studentId}`),
        categories.length ? Promise.resolve({ data: { categories } }) : api.get(`/portfolio-docs/categories`),
      ]);
      setRows(d.data);
      if (!categories.length) setCategories(c.data.categories);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (studentId) load(); /* eslint-disable-next-line */ }, [studentId]);

  const openAdd = () => {
    setForm({
      title: "", category: "worksheet", subject: "",
      document_date: new Date().toISOString().slice(0, 10),
      notes: "", file: null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.file) { toast.error("Attach a file"); return; }
    if (!form.title.trim()) { toast.error("Give the document a title"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("student_id", studentId);
      fd.append("title", form.title);
      fd.append("category", form.category);
      if (form.subject) fd.append("subject", form.subject);
      fd.append("document_date", form.document_date);
      fd.append("notes", form.notes || "");
      fd.append("file", form.file);
      await api.post("/portfolio-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to portfolio");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from this portfolio?`)) return;
    try {
      await api.delete(`/portfolio-docs/${r.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const view = async (r) => {
    try {
      const { data } = await api.get(`/portfolio-docs/${r.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: r.file?.content_type || "application/octet-stream" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    }
  };

  const categoryLabel = (id) => categories.find((c) => c.id === id)?.label || id;

  const visible = useMemo(
    () => (filter === "__all__" ? rows : rows.filter((r) => r.category === filter)),
    [rows, filter],
  );

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="portfolio-docs-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-blue-800 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Portfolio artifacts
          </div>
          <div className="mt-1 text-sm text-stone-500">Upload work completed outside HomeSchool Hub. Everything here flows into the year-end Evaluation Packet automatically.</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="rounded-full h-9 w-40" data-testid="portfolio-docs-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openAdd} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="portfolio-docs-upload-btn">
            <FileUp className="w-4 h-4 mr-2" /> Upload document
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {loading && (<div className="text-sm text-stone-500 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>)}
        {!loading && visible.length === 0 && (
          <div className="text-sm text-stone-500 py-8 text-center border border-dashed border-stone-200 rounded-xl">
            No documents yet. Click <strong>Upload document</strong> to add worksheets, art, book reports, and other portfolio work.
          </div>
        )}
        {!loading && visible.length > 0 && (
          <ul className="divide-y divide-stone-100" data-testid="portfolio-docs-list">
            {visible.map((r) => (
              <li key={r.id} className="py-3 flex items-start justify-between gap-3" data-testid={`doc-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-stone-900 truncate">{r.title}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 font-semibold">{categoryLabel(r.category)}</span>
                    {r.subject && <span className="text-stone-500">· {r.subject}</span>}
                    <span className="text-stone-500">· {r.document_date}</span>
                    {r.file?.original_filename && <span className="text-stone-400 truncate">· {r.file.original_filename}</span>}
                  </div>
                  {r.notes && <div className="mt-1 text-xs text-stone-500 leading-snug">{r.notes}</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => view(r)} className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 px-2 py-1 rounded-full hover:bg-blue-50" data-testid={`doc-view-${r.id}`} title="View file">
                    <ExternalLink className="w-3.5 h-3.5" /> View
                  </button>
                  <button onClick={() => del(r)} className="w-7 h-7 rounded-full bg-red-50 text-red-600 hover:bg-red-500 hover:text-white ring-1 ring-red-200 grid place-items-center" data-testid={`doc-delete-${r.id}`} title="Remove from portfolio">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="portfolio-docs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileUp className="w-5 h-5 text-blue-600" /> Upload portfolio document
            </DialogTitle>
            <DialogDescription>
              Add a scan, photo, PDF, or Word doc. It joins this student&apos;s portfolio and shows up in the year-end Evaluation Packet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Butterfly life cycle worksheet" className="mt-2 rounded-xl h-11" data-testid="doc-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="doc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date completed</Label>
                <Input type="date" value={form.document_date} onChange={(e) => setForm({ ...form, document_date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="doc-date" />
              </div>
            </div>
            <div>
              <Label>Subject (optional)</Label>
              {subjects.length ? (
                <Select value={form.subject || "__none__"} onValueChange={(v) => setForm({ ...form, subject: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="doc-subject"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {subjects.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Science" className="mt-2 rounded-xl h-11" data-testid="doc-subject" />
              )}
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Completed at Grandma's house · Great effort on labelling stages" className="mt-2 rounded-xl min-h-[60px]" data-testid="doc-notes" />
            </div>
            <div>
              <Label>File (PDF, image, .doc, .docx, .txt · 15 MB max)</Label>
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="file"
                  accept="application/pdf,image/*,.doc,.docx,text/plain"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  className="text-sm text-stone-500 file:mr-3 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 file:px-3 file:py-1.5 file:font-medium hover:file:bg-blue-100"
                  data-testid="doc-file"
                />
                {form.file && <span className="text-xs text-stone-500 truncate">{form.file.name}</span>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="doc-save">
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</>) : (<><Upload className="w-4 h-4 mr-2" />Save to portfolio</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
